import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from "../../contexts/AuthContext";
import { pdf } from '@react-pdf/renderer';
import { isClientRole, isPrestataireRole } from '../../utils/roleUtils';

// Composants
import SuiviDuMarche from '../../composants/SuiviDuMarche/SuiviDuMarche';
import Consultation from '../../composants/Consultation/Consultation';
import Candidature from '../../composants/Candidature/Candidature';
import Analyse from '../../composants/Analyse/Analyse';
import Marche from '../../composants/Marche/Marche';
import Button from '../../composants/Button/Button';
import ValidationPopUp from '../../composants/ValidationPopUp/ValidationPopUp';
import SuiviCommandePDF from '../../pages/PDF/SuiviCommandePDF';
import TabNav from '../../composants/TabNav/TabNav';
import CompteARebourd from '../../composants/CompteARebourd/CompteARebourd';

// Styles
import './SuiviDeCommande.css';

// Types
import { CommandeType, TechNotesType, CommandeErrorsType } from './types';
import { get } from 'http';

/**
 * Composant SuiviDeCommande pour afficher et gérer les détails d'une commande
 */
const SuiviDeCommande: React.FC = () => {
  // Hooks et context
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Détermination du rôle utilisateur
  const isClientView = isClientRole(user?.role || '');
  const isPrestataireView = isPrestataireRole(user?.role || '');

  // États
  const [commande, setCommande] = useState<CommandeType | null>(null);
  const [selectedPrestataire, setSelectedPrestataire] = useState<number | null>(null);
  const [acceptCGV, setAcceptCGV] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // États des modales
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState<boolean>(false);
  const [isSupprimerModalOpen, setIsSupprimerModalOpen] = useState<boolean>(false);

  // État des notes techniques
  const [techNotes, setTechNotes] = useState<TechNotesType>({});

  // État des erreurs de formulaire
  const [errors, setErrors] = useState<CommandeErrorsType>({});

  // Récupération du type de commande associée à l'opération
  const getCommandeType = (commande: any): 'MS' | 'BC' => {
    // Logique pour déterminer le type basée sur les données API
    return commande?.type || commande?.operation?.type || 'MS';
  };

  const commandeType = getCommandeType(commande);
  console.log('Type de commande:', commandeType);


  // Vérification de la présence des données de formulaire
  const hasFormData = React.useMemo(() => {
    if (!commande || !commande.formulaire) return false;

    // Vérifier si les propriétés essentielles existent
    const hasBasicInfo = commande.formulaire.nomOperation ||
      commande.formulaire.adresse ||
      commande.formulaire.besoin;

    // Vérifier si les sections existent et sont un tableau
    const hasSections = Array.isArray(commande.formulaire.sections) &&
      commande.formulaire.sections.length > 0;

    return hasBasicInfo || hasSections;
  }, [commande]);

  // Détermination de l'onglet actif
  const [activeTab, setActiveTab] = useState<number>(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    return tabParam ? parseInt(tabParam, 10) : 0;
  });

  /**
   * Liste des onglets disponibles selon le rôle de l'utilisateur
   */
  const tabs = useMemo(() => {
    if (isClientView) {
      return ['Historique', 'Consultation', 'Analyse', 'Les pièces du marché', 'Messagerie', 'Évaluation'];
    } else if (isPrestataireView) {
      return ['Historique', 'Candidature', 'Les pièces du marché', 'Messagerie'];
    }
    return ['Historique', 'Consultation', 'Candidature', 'Analyse', 'Les pièces du marché', 'Messagerie', 'Évaluation'];
  }, [isClientView, isPrestataireView]);

  /**
    * Détermine les onglets désactivés en fonction du statut de la commande
    */
  const getDisabledTabs = useCallback(() => {
    if (!commande) return [];

    const disabledTabs: number[] = [];

    const allTabs = tabs;
    const idBloques = [1, 2, 3, 4, 5, 6];

    // if (isPrestataireView) {
    //   if (['En consultation', 'Candidature en cours'].includes(commande.status)) {
    //     const marcheIndex = tabs.indexOf('Les pièces du marché');
    //     if (marcheIndex !== -1) disabledTabs.push(marcheIndex);
    //   }
    // } else if (isClientView) {
    //   if (commande.status === 'En attente de l\'analyse') {
    //     const marcheIndex = tabs.indexOf('Les pièces du marché');
    //     if (marcheIndex !== -1) disabledTabs.push(marcheIndex);
    //   }
    // }

    if (!idBloques.includes(commande.status.id)) {
      // Pour rendre Les pièces du marché inaccessible tant que l'analyse n'est pas clôturée
      const marcheIndex = allTabs.indexOf('Les pièces du marché');
      if (marcheIndex !== -1) disabledTabs.push(marcheIndex);
    }

    return disabledTabs;
  }, [commande, tabs, isClientView, isPrestataireView]);

  const getInactiveTabs = useCallback(() => {
    if (!commande) return [];
    const inactiveTabs: number[] = [];
    const allTabs = tabs;

    const messagerieIndex = allTabs.indexOf('Messagerie');
    const evaluationIndex = allTabs.indexOf('Évaluation');
    if (messagerieIndex !== -1) inactiveTabs.push(messagerieIndex);
    if (evaluationIndex !== -1) inactiveTabs.push(evaluationIndex);

    return inactiveTabs;
  }, [commande, tabs]);
  /**
   * Formate une date de l'API au format français
   */
  const formatApiDate = useCallback((apiDate: string): string => {
    if (!apiDate) return '';
    try {
      const date = new Date(apiDate);
      return date.toLocaleDateString('fr-FR');
    } catch (e) {
      return '';
    }
  }, []);

  /**
   * Récupère la couleur correspondant au statut
   */
  const getStatusColor = useCallback((status: string): string => {
    const statusColors = {
      'Valider': '#00A708',
      'Brouillon': '#6B7280',
      'Signer': '#F4751B',
      'Analyser': '#F4751B',
      'Refuser': '#D90E0E',
      'Expirer': '#D90E0E',
      'En attente': '#6B7280'
    };

    return statusColors[status] || '#6B7280';
  }, []);

  /**
   * Charge les détails de la commande depuis l'API
   */
  useEffect(() => {
    const fetchCommandeDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Aucun token d\'authentification trouvé');
        }

        // Récupération des données de la commande
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/commande/${id}`, {
          headers:
          {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }

        const data = await response.json();
        if (data.status !== 'success' || !data.data) {
          throw new Error('Format de réponse API inattendu');
        }

        const subsequentData = data.data;

        // Transformation des données
        // const formattedCommande = await formatCommandeData(subsequentData, token);
        // setCommande(formattedCommande);
        setCommande(subsequentData)

        // Sélection du premier prestataire par défaut
        // if (formattedCommande.prestataires?.length > 0) {
        //   setSelectedPrestataire(0);
        // }

        // Initialisation des notes techniques
        initializeTechNotes(subsequentData);

      } catch (err: any) {
        setError(err.message || 'Une erreur est survenue lors de la récupération des détails de la commande');
        console.error('Erreur de chargement de la commande:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCommandeDetails();
  }, [id, isClientView, formatApiDate]);

  /**
   * Formatte les données de la commande depuis l'API
   */
  const formatCommandeData = async (subsequentData: any, token: string): Promise<CommandeType> => {
    // Récupération des informations du lot technique
    let lotTechniqueInfo = '';
    if (subsequentData.lot_technique) {
      lotTechniqueInfo = subsequentData.lot_technique.id
        ? `${subsequentData.lot_technique.id} - ${subsequentData.lot_technique.nom}`
        : subsequentData.lot_technique.nom || 'Non spécifié';
    }

    // Initialisation des données
    let lotGeographiqueInfo = '';
    let nomDepartementInfo = '';
    let prestatairesInfo = [];
    let clientInfo = {
      organisme: subsequentData.organisme_nom || '',
      contact: '',
      email: '',
      telephone: ''
    };

    try {
      // Récupération des informations du département
      if (subsequentData.id_lot_departement) {
        const departementResponse = await fetch(`${process.env.REACT_APP_API_URL}/departements/${subsequentData.id_lot_departement}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (departementResponse.ok) {
          const departementData = await departementResponse.json();
          if (departementData.status === 'success' && departementData.data) {
            lotGeographiqueInfo = departementData.data.code || subsequentData.id_lot_departement.toString();
            nomDepartementInfo = departementData.data.nom || '';
          }
        }
      }

      // Récupération des informations des prestataires
      if (subsequentData.commandes && Array.isArray(subsequentData.commandes)) {
        for (const commande of subsequentData.commandes) {
          if (commande.id_contrat) {
            const contratResponse = await fetch(`${process.env.REACT_APP_API_URL}/contrats/${commande.id_contrat}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              }
            });

            if (contratResponse.ok) {
              const contratData = await contratResponse.json();
              if (contratData.status === 'success' && contratData.data && contratData.data.entite) {
                prestatairesInfo.push({
                  organisme: contratData.data.entite.nom || '',
                  contact: '',
                  email: '',
                  telephone: '',
                  responseTechnique: subsequentData.reponse || subsequentData.reponse_technique || ''
                });
              }
            }
          }
        }
      }
    } catch (error) {
      console.warn("Erreur lors de la récupération des infos supplémentaires:", error);
    }

    // Formatage des articles
    const articlesFormatted = (subsequentData.articles || []).map(article => ({
      id: article.id?.toString() || '',
      numero: article.code?.toString() || '',
      prestation: article.nom || '',
      description: article.description,
      unite: article.unite || '',
      quantite: 1,
      totalHT: article.pivot?.prix || 0
    }));

    // Vérifier si nous avons les données d'opération
    const hasOperationData = subsequentData.operation && typeof subsequentData.operation === 'object';

    // Préparation de l'objet formulaire en utilisant les données de l'opération si disponibles
    const formulaireData = {
      nomOperation: hasOperationData ? subsequentData.operation.nom || '' : subsequentData.nom || '',
      referenceInterne: hasOperationData ? subsequentData.operation.reference || '' : subsequentData.reference || '',
      delaisConsultation: hasOperationData ? subsequentData.operation.delais || 30 : subsequentData.delais || 30,
      echeance: hasOperationData ? subsequentData.operation.echeance || '' : subsequentData.echeance || '',
      delaisFinOperation: hasOperationData ? subsequentData.operation.date_fin_operation || subsequentData.operation.delais || 30 : subsequentData.delais_fin_operation || subsequentData.delais || 30,
      besoin: hasOperationData ? subsequentData.operation.besoin || '' : subsequentData.besoin || '',
      visitePreavis: hasOperationData ? subsequentData.operation.visite || false : subsequentData.visite || false,
      adresse: hasOperationData ? subsequentData.operation.adresse || '' : subsequentData.adresse || '',
      codePostal: hasOperationData ? subsequentData.operation.code_postal || '' : subsequentData.code_postal || '',
      ville: hasOperationData ? subsequentData.operation.ville || '' : subsequentData.ville || '',
      coordonneesMaitrise: hasOperationData ? subsequentData.operation.coordonnees_maitrise || '' : subsequentData.coordonnees_maitrise || '',
      coordonneesMaitriseDOuvrage: hasOperationData ? subsequentData.operation.coordonnees_maitrise_ouvrage || '' : subsequentData.coordonnees_maitrise_ouvrage || '',
      dateDebutOperation: hasOperationData ? subsequentData.operation.date_debut_operation || '' : subsequentData.date_debut_operation || '',
      sections: hasOperationData && Array.isArray(subsequentData.sections)
        ? subsequentData.sections.map((section: any) => ({
          id: section.id?.toString() || section.id,
          nom: section.name || '',
          articles: Array.isArray(section.articles)
            ? section.articles.map((article: any) => ({
              id: article.id?.toString() || article.id,
              nom: article.nom || '',
              code: article.code || article.id,
              description: article.description || ''
            }))
            : []
        }))
        : (Array.isArray(subsequentData.articles) && subsequentData.articles.length > 0 && subsequentData.articles[0].section
          ? [{
            id: subsequentData.articles[0].section.id?.toString() || subsequentData.articles[0].section.id,
            nom: subsequentData.articles[0].section.name || '',
            articles: subsequentData.articles.map((article: any) => ({
              id: article.id?.toString() || article.id,
              nom: article.nom || '',
              code: article.code || article.id,
              description: article.description || ''
            }))
          }]
          : [])
    };

    // Construction de l'objet commande
    return {
      id: subsequentData.id?.toString() || '',
      numeroDeCommande: subsequentData.numero || `MS-${subsequentData.id}`,
      titre: subsequentData.nom || '',
      lotTechnique: lotTechniqueInfo,
      lotGeographique: lotGeographiqueInfo || subsequentData.id_lot_departement?.toString() || '',
      NomDepartement: nomDepartementInfo,
      status: subsequentData.status.nom,
      type: 'Sub',
      dateCreation: formatApiDate(subsequentData.created_at),
      dateValidation: formatApiDate(subsequentData.updated_at),
      client: clientInfo,
      prestataires: prestatairesInfo.length > 0 ? prestatairesInfo : [
        {
          organisme: "Information non disponible",
          contact: "",
          email: "",
          telephone: "",
          responseTechnique: subsequentData.reponse || subsequentData.reponse_technique || ""
        }
      ],
      articles: articlesFormatted,
      echanges: [],
      formulaire: formulaireData,
      noteTechnique: subsequentData.note_technique ? {
        operationnel: {
          note: subsequentData.note_technique.note_operationnel || 0,
          commentaire: subsequentData.note_technique.commentaire_operationnel || '',
          ponderation: subsequentData.note_technique.ponderation_operationnel || 5
        },
        humain: {
          note: subsequentData.note_technique.note_humain || 0,
          commentaire: subsequentData.note_technique.commentaire_humain || '',
          ponderation: subsequentData.note_technique.ponderation_humain || 4
        },
        environnement: {
          note: subsequentData.note_technique.note_environnement || 0,
          commentaire: subsequentData.note_technique.commentaire_environnement || '',
          ponderation: subsequentData.note_technique.ponderation_environnement || 1
        },
        noteGlobale: subsequentData.note_technique.note_technique || 0
      } : undefined,
      noteFinanciere: subsequentData.note_financiere || 0,
      noteTotale: subsequentData.note_total || 0,
      statusInfo: subsequentData.status ? {
        id: subsequentData.status.id,
        action: subsequentData.status.action,
        nom: subsequentData.status.nom,
        delai: subsequentData.status.delai,
        typeCompte: subsequentData.status.type_compte
      } : undefined,
      delaiRestant: subsequentData.status && subsequentData.status.delai ?
        `${Math.floor(subsequentData.status.delai / 86400)}j ${Math.floor((subsequentData.status.delai % 86400) / 3600)}h` :
        undefined,
      delais: subsequentData.delais
    };
  };

  /**
   * Initialise les notes techniques à partir des données de l'API
   */
  const initializeTechNotes = (subsequentData: any) => {
    if (subsequentData.commandes && Array.isArray(subsequentData.commandes)) {
      const techNotesInit = {};

      subsequentData.commandes.forEach((commande, index) => {
        if (commande.note_technique) {
          techNotesInit[index.toString()] = {
            "organisation": {
              note: commande.note_technique.note_operationnel || 0,
              commentaire: commande.note_technique.commentaire_operationnel || ''
            },
            "moyens": {
              note: commande.note_technique.note_humain || 0,
              commentaire: commande.note_technique.commentaire_humain || ''
            },
            "social": {
              note: commande.note_technique.note_environnement || 0,
              commentaire: commande.note_technique.commentaire_environnement || ''
            }
          };
        }
      });

      setTechNotes(techNotesInit);
    }
  };

  /**
   * Valide le formulaire de consultation
   */
  const validateConsultationForm = useCallback(() => {
    const newErrors: CommandeErrorsType = {};

    if (!commande?.formulaire?.besoin?.trim()) {
      newErrors.besoin = "La définition du besoin est obligatoire";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [commande]);

  /**
   * Lance la consultation après validation
   */
  const handleLancerConsultation = useCallback(async () => {
    if (!validateConsultationForm()) {
      const errorElement = document.querySelector('.error');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      // Récupération des données de l'opération
      const operationResponse = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!operationResponse.ok) {
        throw new Error('Erreur lors de la récupération des données de l\'opération');
      }

      const operationData = await operationResponse.json();
      const subsequentData = operationData.data;

      // Lancement de la consultation
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/${id}/consultation`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          commande: commande,
          id_lot_departement: subsequentData.id_lot_departement,
          id_lot_technique: subsequentData.id_lot_technique
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors du lancement de la consultation');
      }

      alert("La consultation a été lancée. Les prestataires seront notifiés.");
      window.location.reload();
    } catch (error) {
      console.error('Erreur lors du lancement de la consultation:', error);
      alert('Erreur lors du lancement de la consultation. Veuillez réessayer.');
    }
  }, [commande, id, validateConsultationForm]);

  /**
   * Calcule le score financier pour chaque prestataire
   */
  const calculateFinancialScore = useCallback((prestataires: any[]): { [key: string]: number } => {
    if (!prestataires || prestataires.length === 0) return {};

    const montants = prestataires.map((p, idx): { id: string; montant: number } => ({
      id: idx.toString(),
      montant: 10000 + (idx * 1000)
    }));

    const minMontant = Math.min(...montants.map(m => m.montant));
    const maxMontant = Math.max(...montants.map(m => m.montant));
    const scores: { [key: string]: number } = {};

    if (montants.length === 1) {
      scores[montants[0].id] = 10;
    } else if (montants.length === 2) {
      montants.forEach(m => {
        scores[m.id] = m.montant === minMontant ? 10 : 0;
      });
    } else {
      montants.forEach(m => {
        if (m.montant === minMontant) {
          scores[m.id] = 10;
        } else if (m.montant === maxMontant) {
          scores[m.id] = 0;
        } else {
          const plageTotal = maxMontant - minMontant;
          const position = maxMontant - m.montant;
          scores[m.id] = Math.round((position / plageTotal) * 10);
        }
      });
    }

    return scores;
  }, []);

  /**
   * Calcule la note globale pour un prestataire
   */
  const getNoteGlobale = useCallback((techNotes, prestaId) => {
    const criteres = ['organisation', 'moyens', 'social'];
    let totalPoints = 0;
    let maxPossiblePoints = 0;

    const pointsMax = {
      'organisation': 5,
      'moyens': 4,
      'social': 1
    };

    criteres.forEach(critere => {
      const note = techNotes[prestaId]?.[critere]?.note || 0;
      totalPoints += note;
      maxPossiblePoints += pointsMax[critere];
    });

    return maxPossiblePoints > 0 ? ((totalPoints / maxPossiblePoints) * 10).toFixed(2) : "0.00";
  }, []);

  /**
   * Exporte la commande au format PDF
   */
  const handleExport = useCallback(async () => {
    if (!commande) return;

    const blob = await pdf(<SuiviCommandePDF commande={commande} />).toBlob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `suivi-commande-${commande.numeroDeCommande || commande.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, [commande]);

  /**
   * Gère la confirmation du refus de commande
   */
  const handleRefuseConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/${id}/refuser`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du refus de la commande');
      }

      window.location.reload();
    } catch (error) {
      console.error('Erreur lors du refus de la commande:', error);
      alert('Erreur lors du refus de la commande. Veuillez réessayer.');
    } finally {
      setIsRefuseModalOpen(false);
    }
  }, [id]);

  /**
   * Gère la confirmation de suppression de commande
   */
  const handleSupprimerConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });


      if (!response.ok) {
        throw new Error('Erreur lors de la suppression de la commande');
      }

      navigate('/commandes');
    } catch (error) {
      console.error('Erreur lors de la suppression de la commande:', error);
      alert('Erreur lors de la suppression de la commande. Veuillez réessayer.');
    } finally {
      setIsSupprimerModalOpen(false);
    }
  }, [id, navigate]);

  /**
   * Gère le changement d'onglet
   */
  const handleTabChange = useCallback((tabIndex: number) => {
    const disabledTabs = getDisabledTabs();
    const inactiveTabs = getInactiveTabs();
    if (!disabledTabs.includes(tabIndex) || !inactiveTabs.includes(tabIndex)) {
      setActiveTab(tabIndex);
    }
  }, [getDisabledTabs, getInactiveTabs]);

  /**
   * Finalise la commande
   */
  const handleFinaliserCommande = useCallback(() => {
    navigate(`/commande/${id}/validation`);
  }, [id, navigate]);

  // Créez un useMemo en dehors de renderTabContent
  const candidatureComponent = useMemo(() => {
    if (!commande) return null;

    return (
      <Candidature
        commande={commande}
        setCommande={setCommande}
        handleExport={handleExport}
        viewOnly={!['En consultation', 'Consultation en cours'].includes(commande?.status?.nom || '')}
      />
    );
  }, [commande?.id, commande?.status?.nom, handleExport]);

  /**
   * Affiche le contenu de l'onglet actif
   */
  const renderTabContent = () => {
    const isConsultationReadOnly = commande?.status?.nom !== 'Brouillon';
    const isAnalyseReadOnly = ['En attente de votre signature', 'Accepté', 'Refusé'].includes(commande?.status?.nom || '');

    if (commandeType === 'MS') {
      // Pour les prestataires, l'onglet "Les pièces du marché" est à l'index 2
      if (isPrestataireView && activeTab === 2) {
        return <Marche commande={commande} selectedPrestataire={selectedPrestataire} />;
      }

      // Pour les prestataires, l'onglet "Candidature" est à l'index 1
      if (isPrestataireView && activeTab === 1) {
        return candidatureComponent;
      }

      switch (activeTab) {
        case 0: // Suivi du Marché
          return <SuiviDuMarche commande={commande} type="MS" />;

        case 1: // Consultation ou Candidature
          if (isPrestataireView) {
            return candidatureComponent;
          } else {
            return (
              <Consultation
                commande={commande}
                errors={errors}
                setErrors={setErrors}
                setCommande={setCommande}
                acceptCGV={acceptCGV}
                setAcceptCGV={setAcceptCGV}
                handleExport={handleExport}
                validateConsultationForm={validateConsultationForm}
                handleLancerConsultation={handleLancerConsultation}
                viewOnly={isConsultationReadOnly}
                hasFormData={hasFormData}
              />
            );
          }

        case 2: // Analyse pour les clients
          if (isClientView) {
            return (
              <Analyse
                commande={commande}
                techNotes={techNotes}
                setTechNotes={setTechNotes}
                selectedPrestataire={selectedPrestataire}
                setSelectedPrestataire={setSelectedPrestataire}
                isDetailsModalOpen={isDetailsModalOpen}
                setIsDetailsModalOpen={setIsDetailsModalOpen}
                handleExport={handleExport}
                calculateFinancialScore={calculateFinancialScore}
                getNoteGlobale={getNoteGlobale}
                handleFinaliserCommande={handleFinaliserCommande}
                viewOnly={isAnalyseReadOnly}
              />
            );
          }
          break;

        case 3: // Les pièces du marché pour les clients
          return <Marche commande={commande} selectedPrestataire={selectedPrestataire} />;
      }
    } else if (commandeType === 'BC')
      // Logique existante pour BC
      switch (activeTab) {
        case 0:
          return <SuiviDuMarche commande={commande} type="BC" />;
        // ...
      }

  };

  // Affichage selon l'état de chargement
  if (loading) {
    return <div className="loading-indicator">Chargement des détails de la commande...</div>;
  }

  if (error && !commande) {
    return (
      <div className="error-message">
        <p>Erreur: {error}</p>
        <p>Impossible de charger les détails de cette commande.</p>
      </div>
    );
  }

  if (!commande) {
    return <div className="empty-state">Aucune commande trouvée avec cet identifiant.</div>;
  }

  // Titre de la page
  const titre = (commande.status === 'Accepté' ? 'Marché Subséquent' : 'Projet de Marché Subséquent')
    + ` ${commande.numeroDeCommande || commande.id}`;

  return (
    <div className="suivi-commande">
      <div className="suivi-commande-title">
        <button
          className="back-button"
          onClick={() => navigate(-1)}
          aria-label="Retour"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="45" height="45" viewBox="0 0 55 55" fill="none">
            <circle cx="27.5" cy="27.5" r="27.5" fill="#FFFDFC" />
            <path d="M34.7369 13.0263L20.2632 27.5L34.7369 41.9737" stroke="#2C3948" strokeWidth="4" strokeLinecap="round" />
          </svg>
        </button>
        <h1 title={commande?.operation?.nom}>{commande?.operation?.nom}</h1>
        <h2>Suivi de Marché Subséquent - {commande.operation.numero}</h2>
        <div className="subtitle">
          <span>{commande.operation.lot_technique.id} - {commande.operation.lot_technique.nom}, {commande.operation.lot_departement.id} - {commande.operation.lot_departement.departement.nom}</span>
        </div>
        <div className='status-container'>
          <div className='status-header'>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 45 41" fill="none">
              <ellipse
                cx="22.5"
                cy="20.5"
                rx="22.5"
                ry="20.5"
                fill={getStatusColor(commande.status.color)}
              />
            </svg>
            <span className={`status-${commande.status.nom.toLowerCase().replace(/\s+/g, '-')}`}>
              {commande.status.nom}
            </span>
          </div>
          {commande.delais && (
            <span className='countdown-container'>
              <CompteARebourd targetDate={commande.delais} />
            </span>
          )}
        </div>

      </div>

      <div className="suivi-de-commande-content">
        <TabNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          disabledTabs={getDisabledTabs()}
          inactiveTabs={getInactiveTabs()}
        />

        <div className="tab-content">
          {renderTabContent()}
        </div>

        <ValidationPopUp
          isOpen={isRefuseModalOpen}
          title="Refus de la commande"
          text="Êtes-vous sûr de vouloir refuser cette commande ?"
          onClose={() => setIsRefuseModalOpen(false)}
          onConfirm={handleRefuseConfirm}
          cancelLabel="non"
          confirmLabel="oui"
        />

        <ValidationPopUp
          isOpen={isSupprimerModalOpen}
          title="Suppression de la commande"
          text="Êtes-vous sûr de vouloir supprimer cette commande ?"
          onClose={() => setIsSupprimerModalOpen(false)}
          onConfirm={handleSupprimerConfirm}
          cancelLabel="non"
          confirmLabel="oui"
        />
      </div>
    </div>
  );
};

export default SuiviDeCommande;